import {
    OperationVariables,
    QueryLazyOptions,
    useMutation,
    useReactiveVar,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { TextField } from '@material-ui/core'
import clsx from 'clsx'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
    stripDoubleSpaces,
    stripDoubleSpacesTrimStart,
} from 'shared/utils/inputUtils'
import { validateFamilyNumber } from '../components/common'
import { AutoCompleteInputMultiSelect } from './AutoCompleteInput'
import {
    AddProductDialogState,
    addProductDialogState,
    categoriesOptionsState,
    setProductDialogState,
} from './addProductFormHandler'
import { useAddProductFormStyles } from './formStyles'

type Props = {
    callAutoSuggest: (
        options?: QueryLazyOptions<OperationVariables> | undefined
    ) => void
    autoSuggestData: any
    isCategoriesLoading: boolean
}

const NewFamilySection: FC<Props> = ({
    callAutoSuggest,
    autoSuggestData,
    isCategoriesLoading,
}) => {
    const classes = useAddProductFormStyles()
    const {
        categories,
        errors,
        firmName,
        sectionNumber,
        sectionTitle,
        type,
    } = useReactiveVar(addProductDialogState)
    const categoriesOptions = useReactiveVar(categoriesOptionsState)
    const [isErrorShown, setIsErrorShown] = useState(false)

    const isFamilyDisabled = useMemo(() => !firmName, [firmName])
    const isCategoryDisabled = useMemo(
        () => (!sectionNumber && !sectionTitle) || !!errors.sectionNumber,
        [errors, sectionNumber, sectionTitle]
    )

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const handleFamilyNumberChange = useCallback(
        (e) => {
            if (!!isErrorShown) {
                setIsErrorShown(false)
            }
            const tempValue = e?.target?.value?.replace(/[^0-9\.]/g, '') ?? ''
            const isFamilyNumberValid = validateFamilyNumber(tempValue)

            let tempState: Partial<AddProductDialogState> = {
                sectionNumber: tempValue,
            }

            if (!isFamilyNumberValid) {
                tempState = {
                    ...tempState,
                    errors: {
                        ...errors,
                        sectionNumber: 'Invalid Section Number',
                    },
                }
            }

            if (!!errors.sectionNumber && !!isFamilyNumberValid) {
                tempState = {
                    ...tempState,
                    errors: { ...errors, sectionNumber: '' },
                }
            }

            setProductDialogState({
                ...tempState,
            })

            if (!!isFamilyNumberValid) {
                callAutoSuggest({
                    variables: {
                        input: {
                            listingType: type,
                            familyName: stripDoubleSpaces(
                                tempState.sectionNumber
                            ),
                            productType: '',
                            isProductTypeSearch: false,
                            limit: 1,
                        },
                    },
                })
            }
        },
        [
            setProductDialogState,
            validateFamilyNumber,
            errors,
            callAutoSuggest,
            type,
            sectionNumber,
            sectionTitle,
            isErrorShown,
            setIsErrorShown,
            stripDoubleSpaces,
        ]
    )

    const handleFamilyTitleChange = useCallback(
        (e) => {
            setProductDialogState({
                sectionTitle: stripDoubleSpacesTrimStart(e?.target.value),
            })
        },
        [setProductDialogState, setIsErrorShown, stripDoubleSpacesTrimStart]
    )

    const handleCategoriesChange = useCallback(
        (_, value) => {
            setProductDialogState({ categories: value })
        },
        [setProductDialogState]
    )

    const noop = useCallback(() => {}, [])

    useEffect(() => {
        if (
            !!sectionNumber &&
            !!autoSuggestData?.productListingDetailsAutoSuggest
                ?.productFamilyDetails?.length &&
            autoSuggestData?.productListingDetailsAutoSuggest?.productFamilyDetails.some(
                (family: Record<string, string>) =>
                    family.sectionNumber === sectionNumber.trim()
            ) &&
            !isErrorShown
        ) {
            setIsErrorShown(true)
            setProductDialogState({
                errors: {
                    sectionNumber: 'Duplicate Family',
                },
            })

            showGlobalSnackbar({
                variables: {
                    message: 'Duplicate family. Please enter a different one',
                    variant: 'error',
                    id: 'AddProduct_Duplicate_Family',
                },
            })
        }
    }, [
        isErrorShown,
        setIsErrorShown,
        autoSuggestData,
        setProductDialogState,
        showGlobalSnackbar,
        sectionNumber,
    ])

    return (
        <>
            <GridItem
                className={clsx(classes.rowContainer, 'rowContainer')}
                xs={3}
                data-testid="sectionNumber"
            >
                <TextField
                    className={classes.inputField}
                    fullWidth
                    value={sectionNumber}
                    variant="outlined"
                    error={!!errors.sectionNumber}
                    disabled={isFamilyDisabled}
                    onChange={handleFamilyNumberChange}
                    label="Family Number"
                />
            </GridItem>
            <GridItem
                className={clsx(classes.rowContainer, 'rowContainer')}
                data-testid="sectionTitle"
                xs={9}
            >
                <TextField
                    fullWidth
                    className={classes.inputField}
                    disabled={isFamilyDisabled}
                    value={sectionTitle}
                    variant="outlined"
                    error={!!errors.sectionTitle}
                    onChange={handleFamilyTitleChange}
                    label="Family Name"
                />
            </GridItem>
            <GridItem
                className={clsx(
                    classes.rowContainer,
                    classes.smallContainer,
                    'rowContainer'
                )}
                xs={7}
                data-testid="category"
            >
                <AutoCompleteInputMultiSelect
                    name="Category"
                    items={categoriesOptions}
                    valid={!errors.categories}
                    disabled={isCategoryDisabled}
                    handleChange={handleCategoriesChange}
                    selectedValues={categories}
                    handleInputChange={noop}
                    loading={isCategoriesLoading}
                />
            </GridItem>
        </>
    )
}

export default NewFamilySection
